import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  ComponentRendering,
  Field,
  Image,
  ImageFieldValue,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sanitize } from 'isomorphic-dompurify';

import {
  Button,
  ButtonVariantType,
  ComponentProps,
  getContrastThemeClass,
  resolveBackgroundStyle,
} from '@gn-audio/jss-components';

import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type ScrollingKSPSpotItem = {
  id: string;
  title: Field<string>;
  backgroundColor: Field<string>;
  description: Field<string>;
  image: Field<ImageFieldValue>;
  firstCtaButtonText: Field<string>;
  firstCtaButtonAriaLabel?: Field<string>;
  firstCtaButtonLink: LinkField;
  firstCtaButtonVariant: Field<string>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonText: Field<string>;
  secondCtaButtonAriaLabel?: Field<string>;
  secondCtaButtonLink: LinkField;
  secondCtaButtonVariant: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
};
export type ScrollingKSpSpotProps = {
  fields: ScrollingKSPSpotRendering;
} & ComponentProps;
export type ScrollingKSPSpotRendering = {
  spots: ScrollingKSPSpotItem[];
  tag: Field<string>;
  background: Field<string>;
  trackingSpotName?: Field<string>;
} & ComponentRendering;

gsap.registerPlugin(ScrollTrigger);

const ScrollingKSPSpot = ({ fields }: ScrollingKSpSpotProps) => {
  const { tag, background } = fields;
  const spotRef = useSpotTracking(fields.trackingSpotName?.value, { threshold: 0.3 });
  const spotThemeClass = getContrastThemeClass(background.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(background.value as string) || {
    backgroundColor: '#ffffff',
  };
  const accentElementColor = spotBackgroundStyle?.background
    ? '#f5f5f5'
    : spotBackgroundStyle?.backgroundColor
      ? `color-mix(in srgb, ${spotBackgroundStyle.backgroundColor} 90%, ${
          spotThemeClass.includes('dark') ? 'white' : 'black'
        } 10%)`
      : undefined;

  const textContainerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const spots = fields.spots || [];

  useGSAP(() => {
    const textContainer = textContainerRef.current!;
    const imageContainer = imageContainerRef.current!;
    const children = Array.from(textContainer.children);
    const images = Array.from(imageContainer.children);
    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
      let wasScrollTriggerRefreshed = false;

      gsap.set(children, { opacity: 0.3 });
      gsap.set(images, { opacity: 0 });

      children.forEach((block, index) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: block,
            start: 'top center',
            end: 'bottom center',
            onToggle: (self) => {
              gsap.to(block, { opacity: self.isActive ? 1 : 0.3, duration: 0.6 });
              gsap.to(imageContainer.children[index], {
                opacity: self.isActive ? 1 : 0,
                duration: 0.6,
              });
            },
            onRefresh: (self) => {
              gsap.set(block, { opacity: self.isActive ? 1 : 0.3 });
              gsap.set(imageContainer.children[index], { opacity: self.isActive ? 1 : 0 });
            },
          },
        });
      });

      gsap.timeline({
        scrollTrigger: {
          trigger: textContainer,
          pin: imageContainer,
          start: 'top 5%',
          end: 'bottom bottom',
          pinSpacing: false,
          onEnter: () => {
            if (!wasScrollTriggerRefreshed) {
              ScrollTrigger.refresh();
              wasScrollTriggerRefreshed = true;
            }
          },
        },
      });
    });

    return () => mm.revert();
  }, [spots]);

  return (
    <div
      ref={spotRef}
      data-wt-spot={fields.trackingSpotName?.value}
      style={spotBackgroundStyle}
      className={classNames(spotThemeClass, 'min-h-screen')}
    >
      <div className="container flex flex-col md:flex-row">
        <div
          style={{
            borderColor: accentElementColor,
          }}
          className="text-container flex-1 border-l-2 pl-6 md:pl-12"
        >
          <div
            className="mb-6 w-fit rounded-full px-4 py-2 md:px-6 md:py-1"
            style={{ background: accentElementColor }}
          >
            {tag?.value}
          </div>
          <div ref={textContainerRef}>
            {spots.map((spot) => (
              <div
                className="flex w-full flex-col justify-center py-8 md:min-h-screen md:w-[420px] md:py-0"
                key={spot.id}
              >
                <h2
                  className="heading-1 font-bold"
                  dangerouslySetInnerHTML={{ __html: sanitize(spot.title.value as string) }}
                />

                <p
                  className="paragraph"
                  dangerouslySetInnerHTML={{ __html: sanitize(spot.description.value as string) }}
                />

                <div className="flex flex-wrap gap-6 pt-5">
                  {spot.firstCtaButtonText?.value && spot.firstCtaButtonLink?.value && (
                    <Button
                      asLink
                      href={spot.firstCtaButtonLink.value.href}
                      variant={spot.firstCtaButtonVariant.value as ButtonVariantType}
                      size="m"
                      {...(spot.firstCtaButtonAriaLabel?.value && {
                        'aria-label': spot.firstCtaButtonAriaLabel.value,
                      })}
                      {...(spot.firstCtaButtonTrackingName?.value && {
                        'data-wt-name': spot.firstCtaButtonTrackingName.value,
                      })}
                    >
                      {spot.firstCtaButtonText.value}
                    </Button>
                  )}
                  {spot.secondCtaButtonText?.value && spot.secondCtaButtonLink?.value && (
                    <Button
                      asLink
                      href={spot.secondCtaButtonLink.value.href}
                      variant={spot.secondCtaButtonVariant.value as ButtonVariantType}
                      size="m"
                      {...(spot.secondCtaButtonAriaLabel?.value && {
                        'aria-label': spot.secondCtaButtonAriaLabel.value,
                      })}
                      {...(spot.secondCtaButtonTrackingName?.value && {
                        'data-wt-name': spot.secondCtaButtonTrackingName.value,
                      })}
                    >
                      {spot.secondCtaButtonText.value}
                    </Button>
                  )}
                </div>
                <Image field={spot.image} className="mt-6 block w-full md:hidden" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
        <div
          className="image-container relative hidden h-screen w-full items-center justify-center md:flex"
          ref={imageContainerRef}
        >
          {spots.map((spot) => (
            <div key={spot.id} className="absolute flex items-center justify-center">
              <Image field={spot.image} className="max-h-[90vh] w-auto" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()(ScrollingKSPSpot);
